import {css} from 'styled-components';
import Colr from 'colr';
import common, {spacing} from '../../common';
import {ThemeSchema} from '../../types';

export const hoverColor = (color: string): string =>
  Colr.fromHex(color)
    .darken(10)
    .toHex();
export const activeColor = (color: string): string =>
  Colr.fromHex(color)
    .darken(20)
    .toHex();

export const disabledTextColor = (color: string): string => {
  const rgb = Colr.fromHex(color)
    .toRgbArray()
    .toString();
  return `rgba(${rgb}, .3)`;
};

const typeScale = {
  1: '2rem',
  2: '1.764rem',
  3: '1.512rem',
  4: '1.26rem',
  5: '1.134rem',
  6: '1rem',
  7: '.882rem',
  8: '.819rem'
};

const lineHeights = [1, 1.2, 1.45];

const tracking = {
  normal: 'normal'
};

const fonts = {
  glober: '"Glober", sans-serif'
};

const fontWeights = {
  400: 400,
  600: 600,
  700: 700
};

const colors = {
  brand: {
    base: '#063f7a',
    light: '#0858ac',
    dark: '#032c4e'
  },
  accent: '#00837c',
  shades: {
    black: '#242424',
    darkest: '#4d4f54',
    darker: '#626366',
    dark: '#979797',
    light: '#e6e7e8',
    lighter: '#eef2f6',
    lightest: '#f4f4f4',
    nearWhite: '#fafafa',
    white: '#ffffff'
  },
  focus: 'rgba(0,131,124,0.15)',
  error: '#c31818',
  success: '#0aad9f',
  info: '#005bb9',
  product: {
    hospital: '#1154A7',
    extras: '#1154A7',
    combined: '#1154A7',
    highlight: {
      backgroundColor: '#063f7a',
      textColor: '#ffffff'
    }
  }
};

const focus = {
  outlineColor: colors.focus,
  outlineStyle: 'solid',
  outlineWidth: '2px',
  outlineOffset: '0.125em'
};

const baseButtonStyles = css`
  box-sizing: border-box;
  cursor: pointer;
  border-radius: 0.3em;
  font-family: ${fonts.glober};
  font-weight: ${fontWeights[700]};
  transition-duration: 0.25s;
  transition-timing-function: ease-in-out;
  transition-property: background;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  -webkit-tap-highlight-color: transparent;
  -webkit-appearance: none;
  border: none;
  display: inline-block;
  text-decoration: none;
  vertical-align: middle;

  display: inline-block;
  border: 0;
  outline: 0;
  cursor: pointer;
  transition: all 0.2s;
  user-select: none;

  &:disabled {
    opacity: 0.55;
    cursor: no-drop;
  }

  &:focus {
    outline-width: ${focus.outlineWidth};
    outline-style: ${focus.outlineStyle};
    outline-color: ${focus.outlineColor};
    outline-offset: ${focus.outlineOffset};
  }
`;

const buttonStyles = {
  primary: {
    normal: css`
      ${baseButtonStyles};

      color: ${colors.shades.white};
      background-color: ${colors.brand.light};
      background: linear-gradient(to bottom, #00a59c, #00625d);
      transition: transform 0.2s ease-out, background 0.3s ease-out;

      &:hover {
        background-color: #006963;
        background: linear-gradient(to bottom, #00afa5, #006c67);
        transform: translate(0, -3px);
      }

      &:active {
        background-color: ${colors.accent};
        transform: translate(0, 0);
      }
    `,
    selected: css``,
    disabled: css`
      &:hover {
        transform: none;
      }
    `
  },
  secondary: {
    normal: css`
      ${baseButtonStyles}
      background-color: ${colors.shades.white};
      color: ${colors.brand.base};
      box-shadow: inset 0 0 0 1px ${colors.brand.base};

      &:hover {
        background-color: ${colors.brand.light};
        color: ${colors.shades.white};
        box-shadow: inset 0 0 0 1px ${colors.brand.light};
      }

      &:active {
        background-color: ${colors.brand.base};
        color: ${colors.shades.white};
        box-shadow: inset 0 0 0 1px ${colors.brand.base};
      }
    `,
    selected: css`
      background-color: ${colors.brand.base};
      color: ${colors.shades.white};
      box-shadow: inset 0 0 0 1px ${colors.brand.base};
    `,
    disabled: css`
      box-shadow: none;
      border: 1px dashed #b3b3b3;

      &:hover {
        background-color: ${colors.shades.white};
        color: ${colors.brand.base};
        box-shadow: none;
        border: 1px dashed #b3b3b3;
      }
    `
  },
  small: css`
    padding: ${spacing[3]};
    font-size: ${typeScale[7]};
    line-height: 1;
  `,
  medium: css`
    padding: ${spacing[3]} ${spacing[5]};
    font-size: ${typeScale[6]};
    line-height: ${lineHeights[1]};
  `,
  large: css`
    padding: ${spacing[3]} ${spacing[5]};
    font-size: ${typeScale[5]};
    line-height: 24px;
  `
};

const baseFormValues = {
  borderColor: colors.shades.darker,
  borderColorValid: colors.success,
  borderColorInvalid: colors.error,
  selectedToggleColor: '#063f7b',
  helpTextColor: colors.shades.darker,
  helpTextSize: typeScale[7],
  labelColor: colors.shades.black,
  labelSize: typeScale[5],
  errorMessageTextColor: colors.error,
  infoMessageTextColor: colors.info
};

const apia: ThemeSchema = {
  ...common,
  typeScale,
  fonts,
  colors,
  focus,

  id: 'apia',
  mode: {
    form: 'light' //default
  },

  headings: {
    color: 'currentColor',
    h1: {
      fontFamily: fonts.glober,
      fontSize: typeScale[1],
      lineHeight: lineHeights[1],
      fontWeight: fontWeights[600],
      tracking: tracking.normal,
      uppercase: false
    },
    h2: {
      fontFamily: fonts.glober,
      fontSize: typeScale[2],
      lineHeight: lineHeights[1],
      fontWeight: fontWeights[600],
      tracking: tracking.normal,
      uppercase: false
    },
    h3: {
      fontFamily: fonts.glober,
      fontSize: typeScale[3],
      lineHeight: lineHeights[1],
      fontWeight: fontWeights[600],
      tracking: tracking.normal,
      uppercase: false
    },
    h4: {
      fontFamily: fonts.glober,
      fontSize: typeScale[4],
      lineHeight: lineHeights[1],
      fontWeight: fontWeights[600],
      tracking: tracking.normal,
      uppercase: false
    },
    h5: {
      fontFamily: fonts.glober,
      fontSize: typeScale[5],
      lineHeight: lineHeights[1],
      fontWeight: fontWeights[600],
      tracking: tracking.normal,
      uppercase: false
    },
    h6: {
      fontFamily: fonts.glober,
      fontSize: typeScale[6],
      lineHeight: lineHeights[1],
      fontWeight: fontWeights[600],
      tracking: tracking.normal,
      uppercase: false
    }
  },

  copy: {
    color: colors.shades.black,
    fontFamily: fonts.glober,
    fontSize: typeScale[5],
    fontWeight: fontWeights[400],
    lineHeight: lineHeights[2],
    smallFontSize: typeScale[7],
    largeFontSize: typeScale[4]
  },

  link: {
    loudColor: colors.accent,
    darkColor: colors.shades.black,
    lightColor: colors.shades.white
  },

  button: {
    small: buttonStyles.small,
    medium: buttonStyles.medium,
    large: buttonStyles.large,
    primary: {
      dark: buttonStyles.primary,
      light: buttonStyles.primary
    },
    secondary: {
      dark: buttonStyles.secondary,
      light: buttonStyles.secondary
    }
  },

  forms: {
    light: {
      ...baseFormValues,
      backgroundColor: colors.shades.lightest,
      inputBackgroundColor: colors.shades.white,
      boxShadow: `0 4px 8px 0 ${colors.shades.lighter}`
    },
    white: {
      ...baseFormValues,
      backgroundColor: colors.shades.white,
      inputBackgroundColor: colors.shades.lightest,
      boxShadow: 'none'
    }
  },

  smallFooter: {
    backgroundColor: colors.shades.white,
    textColor: colors.shades.black,
    linkColor: colors.accent
  },

  panel: {
    loud: {
      backgroundColor: colors.brand.base,
      textColor: colors.shades.white
    },
    muted: {
      backgroundColor: colors.shades.lighter,
      textColor: colors.shades.darkest
    },
    light: {
      backgroundColor: colors.shades.lightest,
      textColor: colors.shades.darkest
    },
    dark: {
      backgroundColor: colors.shades.darkest,
      textColor: colors.shades.white
    },
    white: {
      backgroundColor: colors.shades.white,
      textColor: colors.shades.darkest
    }
  }
};

export default apia;
